// Contain models of reservation wizard and team request
import { User } from './dashboard-common.model';
import { XUseSpaceEnum } from '../services/datacontext.service';
import { BookingDetails, ConflictingSpaceBooking } from './bookings.model';
import {
  Amenity,
  Building,
  EventType,
  Floor,
  FloorAmenity,
  Item,
  MemberFirm,
  SpaceFeature,
  SpaceSetupType,
  SpaceType,
  TimeZone
} from './masterdata.model';
import { SafeUrl } from '@angular/platform-browser';
import { ErrorState, LabelPosition, Themes } from '@usitsdasdesign/dds-ng/shared';
import { SelectedOwnerItem } from '../shared/components/owner-search/owner-search.component';
import { TimeSlot } from './meetings.model';
import { ButtonKind, ButtonOptions } from '../shared/components/button/button.component';
import { PositionState } from '../shared/components/tooltip/tooltip.config';

// Models for payload
export interface AvailabilityInput {
  userId: number;
  buildingId: number;
  floorUniqueKey: number;
  preferredFloorUniqueKey: number;
  eventTime: EventTime;
  preferredSpaceTypeId: number;
  attendeesCount: number;
  reservable: boolean;
  categoryType: string;
  searchText: string;
  orderType: string;
  spaceFeatureIds: Array<number>;
  availabilityExcludeBookingIds: Array<number>;
  features: string;
  limit: number;
  page: number;
  isMapViewSelected: boolean;
  useDelegateSpace: string;
  teamRequestId: string;
  includeInaccessibleToUser: boolean;
  includeUnavailable: boolean;
  spaceId: number;
  name: string;
  setupTypeId: number;
  isPrivate: boolean;
  spaceBookingSpecialistId: number;
  primaryContactId: number;
}

export interface EventTime {
  timeSlotType?: string;
  spaceSetupTime?: string;
  spaceTearDownTime?: string;
  setupTime?: string;
  tearDownTime?: string;
  recurrentEventData?: RecurrentEventData;
  singleDayEventData?: SingleDayEventData;
}

export interface RecurrentEventData {
  recurrenceRule: RecurrenceRule;
}

export interface RecurrenceRule {
  recurrencePatternType?: string;
  interval?: number;
  startDate: string;
  endDate: string;
  occurrences?: number;
  weeklyRecurrencePattern: WeeklyRecurrencePattern;
  monthlyRecurrencePattern?: MonthlyRecurrencePattern;
}

export interface WeeklyRecurrencePattern {
  daysOfWeek: Array<string>;
}

export interface SingleDayEventData {
  startDate: string;
  endDate: string;
  isLtr?: boolean;
}

export interface MonthlyRecurrencePattern {
  day: number;
  weekOfMonth: string;
  dayOfWeek: string;
}

export interface MeetingRequestRecommendationInput {
  availabilityInput: AvailabilityInput;
  availabiltyTypes: Array<string>;
}

export interface SpaceAvailabilityFilter extends AvailabilityFilter {
  buildingId: number;
  floorUniqueKey: number;
  preferredFloorUniqueKey: number;
  attendeesCount: number;
  categoryType: string;
  includeUnavailable: boolean;
  searchText: string;
  spaceFeatureIds: Array<number>;
  includeInaccessibleToUser: boolean;
  preferredSpaceTypeId: number;
}

export interface SpaceIdsAvailabilityFilter extends AvailabilityFilter {
  spaceIds: number[];
}

export enum DensityType {
  NotDetermined = 'NotDetermined',
  Empty = 'Empty',
  NearlyEmpty = 'NearlyEmpty',
  HalfFull = 'HalfFull',
  NearlyFull = 'NearlyFull',
  Full = 'Full',
}

export const CLASS_BY_DENSITY_TYPE = {
  [DensityType.NotDetermined]: 'openSeatingNotDetermined',
  [DensityType.Empty]: 'openSeatingEmpty',
  [DensityType.NearlyEmpty]: 'openSeatingNearlyEmpty',
  [DensityType.HalfFull]: 'openSeatingHalfFull',
  [DensityType.NearlyFull]: 'openSeatingNearlyFull',
  [DensityType.Full]: 'openSeatingFull'
};

export interface AvailabilityFilter {
  eventTime: {
    timeSlotType: TimeSlotType;
    spaceSetupTime?: string;
    spaceTearDownTime?: string;
    recurrentEventData?: RecurrentEventData;
    singleDayEventData?: SingleDayEventData;
    spaceBookingTimeSlots?: {
      bookedTimeSlots: BookedTimeSlot[];
    };
  };

  availabilityExcludeBookingIds?: number[];
  features: string;
}

export interface BookedTimeSlot {
  Id: string;
  StartDate: string;
  EndDate: string;
}

// user input models
export interface ReservationDetailsFormValue extends DetailsFormValue {
  virtual: VirtualControlValue[];
  inOffice: {
    items: StandardControlValue[];
  };
  recurringEndDate?: Date;
  teamRequestId?: string;
}

export interface DetailsFormValue {
  reservationDetails: {
    owner: SelectedOwnerItem[];
    allocation?: string;
    searchType: ReservationType;
    typeOfMeeting?: ReservationClassification;
    recurring?: {
      repeatEvery: {
        pattern: RecurrencePatternTypes;
        days: number;
        weeks: number;
        weekDays: {
          [key: string]: boolean;
        };
      };
      endAfter: {
        type: EndAfterType;
        date: Date;
        occurrences: number;
      };
    };
    timeType: TimeType
  };
}

export interface VirtualControlValue {
  typeOfTechnology: string;
  attendees: number;
  startDate: Date;
  endDate: Date;
  startTime: Date;
  endTime: Date;
  timeZone: string;
}

export interface StandardControlValue {
  attendees: number;
  startDate: Date;
  endDate: Date;
  startTime: Date;
  endTime: Date;
  timeZone: TimeZoneWithTZ;
  building: Building;
  floor: number;
  type: string;
  spaceName: any;
  team: TeamRequestDetails;
  setup: number;
  teardown: number;
  setupTeardownPosition: PositionState;
}

// Assignee response is common for RW and TR
export interface AssigneeResponse extends User {
  homeBuildingId: number;
  homeBuildingName: string;
  shortTitle: string;
  area: string;
  fss: string;
  hasConciergeRights: boolean;
  isNonPeopleProfile: boolean;
  isMemberFirmAccount: boolean;
  memberFirm: MemberFirm;
}

export interface UserSearchResult {
  isOverflow: boolean;
  users: AssigneeResponse[];
}

export interface Technology {
  DisplayName: string;
  ActualName: string;
}

interface TimeZoneWithTZ extends TimeZone {
  tzIanaName?: string;
}

export interface TeamRequestDetails {
  id: string;
  teamName: string;
  value?: string; // this description will be assigned floor or team name
}

export interface LocationItem extends Subset {
  title?: string;
  spaceType?: string;
  attendees: number;
  type?: LocationItemType;
  timeZone: {
    TZOffSet: number;
    TZStandardName: string;
    TZIanaName: string;
    TZShortName: string;
  };
  reservationType?: ReservationType;
  team?: TeamRequestDetails;
  building?: Building;
  typeOfTechnology?: string;
  floor?: number;
  spaceName?: string;
  subsets?: Subset[];
  enableWaitlistRequest?: boolean;
  enableMeetingRequest?: boolean;
  datesToFill?: Date[];
}

export interface Subset {
  spaceItem?: SpaceAvailabilityItem;
  startDate?: Date;
  endDate?: Date;
  startTime?: Date;
  reservationStartTime?: Date;
  reservationEndTime?: Date;
  endTime?: Date;
  setup?: number;
  teardown?: number;
  spaceStartTime?: Date;
  spaceEndTime?: Date;
  waitlist?: boolean;
  meetingRequest?: boolean;
  statusColor?: any; // StatusColor;
  complete?: boolean;
  isDateRange?: boolean;
  selected?: boolean;
  partial?: boolean;
  selectedFeatures?: number[];
  daysOfWeek?: string[];
  subsetsToReserve?: Map<number, SubsetDate>;
}

interface SubsetDate {
  startDate: Date;
  endDate: Date;
}

export interface TeamRequestBookingInput {
  teamRequestId: string;
  dateInterval?: {
    startDate: string;
    endDate: string;
  };
  recurrenceRule?: RecurrenceRule;
}

// enum
export enum ReservationType {
  Standard = 'Standard',
  MultiDayRecurring = 'MultiDayRecurring',
  LTR = 'LTR',
}

export enum BookingReservationType {
  Regular = 'Regular',
  Meeting = 'Meeting',
  Ltr = 'Ltr',
  Lab = 'Lab',
}

export enum ReservationClassification {
  SingleOffice = 'SingleOffice',
  Virtual = 'Virtual',
  Hybrid = 'Hybrid',
}

export const ReservationClassificationType = new Map<string, string>([
  [ReservationClassification[ReservationClassification.SingleOffice], 'Translation.In-office'],
  [ReservationClassification[ReservationClassification.Virtual], 'Translation.Virtual'],
  [ReservationClassification[ReservationClassification.Hybrid], 'Translatin.Hybrid']
]);

export const ClassificationTypeMultiLingual = new Map<string, string>([
  [ReservationClassification[ReservationClassification.SingleOffice], 'Translation.SingleOffice'],
  [ReservationClassification[ReservationClassification.Virtual], 'Translation.Virtual'],
  [ReservationClassification[ReservationClassification.Hybrid], 'Translation.Hybrid']
]);

export enum RecurrencePatternTypes {
  Daily = 'Daily',
  EveryWeekDay = 'EveryWeekDay',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
}

export enum EndAfterType {
  Date = 'Date',
  Occurrences = 'Occurrences',
}

export enum SpaceTypeCategory {
  Individual = 'Individual',
  Meeting = 'Meeting',
}

export enum OrderType {
  Recommendation = 'Recommendation',
  Search = 'Search',
}

export enum Features {
  Undefined = 'Undefined',
  Scoring = 'Scoring',
  TimeSlots = 'TimeSlots',
  Bookings = 'Bookings',
  SpaceFeatures = 'SpaceFeatures',
  ScopedSpaces = 'ScopedSpaces',
}

export enum LocationItemType {
  Standard,
  Virtual,
}

export enum DaysOfWeek {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export const WEEK_DAYS = [
  {
    day: DaysOfWeek.Sunday,
    dayAbbreviation: 'S',
    isChecked: false,
    shortName: 'Sun',
    fullName: 'Sunday'
  },
  {
    day: DaysOfWeek.Monday,
    dayAbbreviation: 'M',
    isChecked: true,
    shortName: 'Mon',
    fullName: 'Monday'
  },
  {
    day: DaysOfWeek.Tuesday,
    dayAbbreviation: 'T',
    isChecked: true,
    shortName: 'Tue',
    fullName: 'Tuesday'
  },
  {
    day: DaysOfWeek.Wednesday,
    dayAbbreviation: 'W',
    isChecked: true,
    shortName: 'Wed',
    fullName: 'Wednesday'
  },
  {
    day: DaysOfWeek.Thursday,
    dayAbbreviation: 'T',
    isChecked: true,
    shortName: 'Thu',
    fullName: 'Thursday'
  },
  {
    day: DaysOfWeek.Friday,
    dayAbbreviation: 'F',
    isChecked: true,
    shortName: 'Fri',
    fullName: 'Friday'
  },
  {
    day: DaysOfWeek.Saturday,
    dayAbbreviation: 'S',
    isChecked: false,
    shortName: 'Sat',
    fullName: 'Saturday'
  }
];

export enum WaitListBannerType {
  //   Indicates whether wait list request available based on results of current recommendation request.
  //   None - No banner should be shown for workspace/meeting
  //   NoPreferredWorkspace - there are no workspaces that exactly match specified criteria;
  //   NoPreferredMeetingSpace - there are no meeting spaces that exactly match specified criteria;
  //   NoDesiredMeetingSpace - there are available meeting spaces; but user don't like them
  None = 'None',
  NoPreferredWorkspace = 'NoPreferredWorkspace',
  NoPreferredMeetingSpace = 'NoPreferredMeetingSpace',
  NoDesiredMeetingSpace = 'NoDesiredMeetingSpace',
}

export enum AvailabilityType {
  Undefined = 'Undefined',
  Available = 'Available',
  Reserved = 'Reserved',
  PartiallyAvailable = 'PartiallyAvailable',
}

export enum TimeSlotType {
  SingleDay = 'SingleDay',
  Recurrent = 'Recurrent',
  BookedTimeSlotCollection = 'BookedTimeSlotCollection',
}

export const MEETING_TYPES = [
  {
    name: 'Translation.SingleOffice',
    value: ReservationClassification.SingleOffice
  },
  {
    name: 'Translation.Hybrid',
    value: ReservationClassification.Hybrid
  },
  {
    name: 'Translation.Virtual',
    value: ReservationClassification.Virtual
  }
];

export const ALLOCATIONS = [
  {
    name: 'Translation.Mine',
    value: XUseSpaceEnum.Delegate
  },
  {
    name: 'Translation.Owner',
    value: XUseSpaceEnum.User
  }
];

export const SEARCH_TYPES = [
  {
    name: 'Translation.Standard',
    value: ReservationType.Standard
  },
  {
    name: 'Translation.Recurring',
    value: ReservationType.MultiDayRecurring
  },
  {
    name: 'Translation.LTR',
    value: ReservationType.LTR
  }
];

export enum ReferenceType {
  Booking = 0,
  MeetingWaitList = 1,
  VirtualBooking = 2,
  Reservation = 3,
  Waitlist = 4,
}

export enum ButtonAction {
  previous,
  next,
  confirm,
}

export enum TimeType {
  default = 'default',
  timeMatch = 'timeMatch',
  timeZoneMatch = 'timeZoneMatch'
}

// Response models - RW
export interface RoomSetupType extends SpaceSetupType {
  iconClass?: string;
}

export interface FloorItem extends Floor {
  availableCount: number;
}

export interface SpaceAvailability {
  items: SpaceAvailabilityItem[];
  totalCount: number;
  waitListBannerType: WaitListBannerType;
  teamRequestFloor?: number;
  spaceFeatureSummary: {
    spaceFeatureId: number;
    spaceCount: number;
  }[];
  floorSpaceSummary: { floorUniqueKey: number; name: string; spaceAvailableCount: number }[];
}

export interface SpaceAvailabilityItem {
  space: SpaceView;
  isFavorite?: boolean;
  inaccessibleToUser?: boolean;
  rank?: number;
  colleagues?: { id: number; firstName: string; lastName: string; email: string };
  availabilityType?: AvailabilityType;
  startDate?: string;
  endDate?: string;
  free?: boolean;
  bookings?: { id: number; startDate: string; endDate: string; tearDownTime: string; setupTime: string; user: Partial<User> };
  timeSlots?: TimeSlot[];
  suggestedAvailableTimeSlot: { startDate: string; endDate: string };
  availableDays?: Array<string>;
  bookedTimeSlotMatches?: { id: number; free: boolean }[];
  teamRequest?: boolean;
  userBookings?: (BookingDetails & { isAttendee?: boolean; isOwner?: boolean })[];
  isOtherCategory?: boolean;
  isBookingAvailable?: boolean;
  conflictingSpaceBookings?: ConflictingSpaceBooking[];
  withoutUserBookings?: boolean;
  spaceAlreadySelected?: boolean;
}

export interface SpaceView {
  id: number;
  name: string;
  setupTime: string;
  tearDownTime: string;
  spaceType: SpaceType;
  centerCoordinates: CenterCoordinates;
  sizeCoordinates: SizeCoordinates;
  buildingId: number;
  buildingDisplayName: string;
  floorUniqueKey: number;
  floorName: string;
  capacity: number;
  scopedSpaceIds: Array<number>;
  isRestricted?: boolean;
  isManaged?: boolean;
  features?: { id: number; name: string; iconKey: string; iconClass?: string }[];
  category?: SpaceTypeCategory;
}

export interface SizeCoordinates {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface CenterCoordinates {
  x: number;
  y: number;
  pointSize: number;
}

export interface FloorBookingInput extends SingleDayEventData {
  userId: number;
  floorUniqueKey: number;
}

// Reservation call payload modal
export interface ReservationInput {
  name: string;
  teamRequestId?: string;
  spaceBookings: SpaceBookingInput[];
  virtualBookings: VirtualBookingInput[];
  meetingWaitLists: MeetingWaitlistInput[];
  eventTypeId?: number;
  type?: string;
  status?: string;
  vip?: boolean;
  pointOfContactId?: number;
  seriesId?: number;
  reservationSpecialistId?: number;
  classification?: string;
  conflictingSpaceBookings?: ConflictingSpaceBooking[];
}

export interface SpaceBookingInput {
  spaceId: number;
  name: string;
  setupTypeId: number;
  isPrivate?: boolean;
  attendeesCount?: number;
  eventTime: EventTime;
  spaceBookingSpecialistId?: number;
  primaryContactId?: number;
}

export interface VirtualBookingInput {
  name: string;
  eventTime: EventTime;
  timeZoneIanaName: string;
  estimatedAttendeesCount: number;
  flexibleDateAndTime?: boolean;
  details?: string;
  status?: string;
  technologyDetails: TechnologyDetails;
  actualAttendeesCount?: number;
  additionalDetailIds?: Array<number>;
  producerId?: number;
  facilitatorId?: number;
}

export interface TechnologyDetails {
  connectMeetingId?: string;
  technology: string;
  link?: string;
  passCode?: string;
  accountId?: string;
  accountName?: string;
}

export interface MeetingWaitlistInput {
  buildingId: number;
  name: string;
  attendeesCount: number;
  floorUniqueKey: number;
  spaceSetupTypeId: number;
  isPrivate: boolean;
  flexibleDateAndTime?: boolean;
  details?: string;
  meetingWaitListRequestType: string;
  spaceFeatureIds: Array<number>;
  eventTime: EventTime;
}

// reservation verification model
export interface CreateReservationResponse {
  id: number;
  upcomingBooking: UpcomingBooking;
  revision: number;
}

export interface UpcomingBooking {
  id: number;
  startDate: string;
  occurrenceDescription: string;
}

// Contain models of reservation wizard and team request
export interface AmenitiesCacheData {
  Amenities: Amenity[];
  LastUpdated: string | Date;
}

export interface FloorPlan {
  mapImage?: SafeUrl;
  amenities?: FloorAmenity[];
}

export interface StepOption {
  heading?: string;
  subHeading?: string;
  selected?: boolean;
  completed?: boolean;
}

export interface TeamRequestBase {
  teamName?: string;
  buildingId: number;
  recurrenceRule: RecurrenceRule;
  users: TeamRequestUser[];
  businessId?: number;
  businessAreaId?: number;
  meetingTypeId?: number;
  neighborhoodId?: string;
}

export interface TeamRequestUser {
  email?: string;
  spaceType?: string;
  userId?: number;
  spaceId?: number;
}

export interface TeamRequestDraft extends TeamRequestBase {
  createDate: string;
  userCount: number;
}

export interface TeamRequest {
  id: number;
  teamName: string;
}

export interface TeamRequestInput {
  term?: string;
  teamName?: string;
  buildingIds?: number[];
  reservationId?: number;
}

export interface TeamRequestAvailabilityInput extends TeamRequestBase {
  preferredFloorUniqueKey: number;
}

export interface TeamRequestAvailability {
  floor: {
    uniqueKey: number;
    name: string;
    description: string;
    neighborhoods: Item[];
  };
  availableSpaceSummary: {
    requestedSpaceCount: number;
    foundSpaceCount: number;
    fullyMetRequestCriteria: boolean;
  };
  availableSpaceTypeCounts: [
    {
      requestedSpaceCount: number;
      foundSpaceCount: number;
      matchType: string;
      spaceType: string;
    }
  ];
  spaceFeatures: SpaceFeature[];
  spaceRecommendations: [
    {
      space: TeamRequestSpace;
      user: User;
    }
  ];
  spaceAvailabilities: TeamRequestSpace[];
  availableWorkstations: number;
  hasSpaceWithoutCoordinates: boolean;
}

export interface FloorPlacement extends TeamRequestAvailability {
  selected: boolean;
  expanded: boolean;
}

export interface TeamRequestSpace extends Item {
  spaceType: SpaceType;
  centerCoordinates: CenterCoordinates;
  sizeCoordinates: SizeCoordinates;
  capacity: number;
  availabilityType: string;
  inaccessibleToUser: boolean;
}

export enum TeamRequestDashboardSorting {
  Created = 'Created',
  TeamName = 'TeamName',
  User = 'User',
  RequestedBy = 'RequestedBy',
  SpaceType = 'SpaceType',
  Date = 'Date',
  StartTime = 'StartTime',
  EndTime = 'EndTime',
}

export enum SpaceTypes {
  Workstation = 'Workstation',
  PrivateSpace = 'PrivateSpace',
  MeetingRoom = 'MeetingRoom',
  OpenSeating = 'OpenSeating',
}

export const SECONDARY_BUTTON_OPTIONS: ButtonOptions = {
  kind: ButtonKind.secondary
};
export const PRIMARY_BUTTON_OPTIONS: ButtonOptions = {
  kind: ButtonKind.primaryLoud
};
export const DARK_BUTTON: ButtonOptions = {
  kind: ButtonKind.primaryLoud
};
export const LIGHT_DARK_BUTTON: ButtonOptions = {
  kind: ButtonKind.primary
};
export const BLUE_THEME = Themes.blue;
export const LABEL_POSITION_EXTERNAL = LabelPosition.external;
export const ERROR_STATE_FOCUS_OUT = ErrorState.focusOut;

export interface FilterItem {
  isChecked: boolean;
  id?: number;
  label?: string;
}

export enum OtherSpaceId {
  OpenSeating = 0,
  Restricted = 1,
}

export enum EventOwnerBooking {
  Meetings,
  InvitedMeetings,
  IndividualSpaces,
}

export interface FloorFilter {
  otherSpaces: OtherSpaceId[];
  ownerBookings: EventOwnerBooking[];
  amenities: Amenity['id'][];
}

export const EVENT_OWNER_BOOKINGS: (FilterItem & { hide: (items: SpaceAvailabilityItem[]) => boolean })[] = [
  {
    label: 'Translation.Meetings',
    isChecked: true,
    id: EventOwnerBooking.Meetings,
    hide: (items) => {
      return !items.some((i) => i.space.category === SpaceTypeCategory.Meeting && i.userBookings && i.userBookings.some((b) => b.isOwner));
    }
  },
  {
    label: 'Translation.Invitedmeetings',
    isChecked: true,
    id: EventOwnerBooking.InvitedMeetings,
    hide: (items) => {
      return !items.some(
        (i) => i.space.category === SpaceTypeCategory.Meeting && i.userBookings && i.userBookings.some((b) => b.isAttendee)
      );
    }
  },
  {
    label: 'Translation.Individualspaces',
    isChecked: true,
    id: EventOwnerBooking.IndividualSpaces,
    hide: (items) => {
      return !items.some(
        (i) => i.space.category === SpaceTypeCategory.Individual && i.userBookings && i.userBookings.some((b) => b.isOwner)
      );
    }
  }
];

export const OTHER_SPACES: (FilterItem & { hide: (items: SpaceAvailabilityItem[]) => boolean })[] = [
  {
    label: 'Translation.Os',
    isChecked: true,
    id: OtherSpaceId.OpenSeating,
    hide: (items) => {
      return !items.some((i) => i.space.spaceType.id === 4); // open seating id
    }
  },
  {
    label: 'Translation.Restrictedspaces',
    isChecked: true,
    id: OtherSpaceId.Restricted,
    hide: (items) => {
      return !items.some((i) => i.inaccessibleToUser);
    }
  }
];

export class UnfulfilledRequest {
  id: string;
  request: TeamRequestDetails;
  user: User;
  requestedBy: User;
  createDate: string;
  spaceType: string;
  recurrenceRule: RecurrenceRule;
  building: Building;
  neighborhoodId: string;
}

export interface CreateMeetingData {
  meetingName: string;
  meetingType: EventType;
  series?: Item;
  pointOfContact?: AssigneeResponse;
  vip?: boolean;
  makePrivate?: boolean;
  roomSetup?: number;
  otherDetails?: string;
}

export enum MatchType {
  All = 'All',
  Partial = 'PartialMatch',
  Match = 'Match',
}
